/**
 * @fileoverview Dark-surface tooltip content matching the chart tooltip aesthetic.
 * Wraps the ShadCN TooltipContent so icon-only controls get a consistent dark tooltip
 * instead of the default light `bg-primary` surface.
 * @module shared/components/ui/DarkTooltipContent
 */

import { ComponentProps, ReactElement } from 'react'
import { TooltipContent } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

type DarkTooltipContentProps = ComponentProps<typeof TooltipContent>

/**
 * Tooltip content rendered on the dark glass surface (mirrors the Recharts hover cards).
 * Forwards all `TooltipContent` props; `className` merges on top of the dark defaults.
 *
 * Note: the arbitrary `bg-[...]` values are written as full static literals so Tailwind's
 * JIT scanner can detect them — never construct these class strings dynamically.
 */
export function DarkTooltipContent({
  className,
  arrowClassName,
  ...props
}: DarkTooltipContentProps): ReactElement {
  return (
    <TooltipContent
      className={cn(
        'border border-border/50 bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] text-foreground',
        className
      )}
      arrowClassName={cn(
        'bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] fill-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)]',
        arrowClassName
      )}
      {...props}
    />
  )
}
